import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";

/**
 * Filtering a widget by several combined watchlists at once.
 *
 * The headline search takes one combined filter and evaluates it as
 * `search_term keyword_op (tags filter_mode tickers)` (newsquawk-headlines,
 * controllers/headlines/search.py): tags match on any overlap, tickers on any
 * word, the search term as a web-search expression. So a combined watchlist
 * whose operators are all OR, wherever they apply, is simply the union of
 * its parts — and a union of such lists is one more union, which fits in a
 * single request. That covers most lists, and every tagged list the builder
 * makes. A list that relies on AND would change meaning if merged, so it
 * still filters on its own.
 */

const hasTags = (query: CategoryQuery) =>
  (query.categories?.length ?? 0) > 0 ||
  (query.tagsWatchlists ?? []).some((tw) => (tw.tags?.length ?? 0) > 0);

const hasTickers = (query: CategoryQuery) =>
  (query.portfolios ?? []).some((p) => (p.tickers?.length ?? 0) > 0);

const hasListKeywords = (query: CategoryQuery) =>
  (query.portfolios ?? []).some((p) => (p.keywords?.length ?? 0) > 0);

const hasTypedKeywords = (query: CategoryQuery) => !!query.searchKeyword?.trim();

/**
 * Whether no AND in this list actually applies.
 *
 * An operator only matters when both of its sides have something in them —
 * a list of tags and keywords set to "tags AND tickers" has no tickers for
 * the AND to bite on. Unset operators are OR, as every matcher reads them.
 */
export function isOrOnlyCombined(query: CategoryQuery): boolean {
  const tags = hasTags(query);
  const tickers = hasTickers(query);
  const keywords = hasListKeywords(query) || hasTypedKeywords(query);

  if (tags && tickers && query.filterMode === "AND") return false;
  if (keywords && (tags || tickers) && query.keywordOp === "AND") return false;
  if (
    hasTypedKeywords(query) &&
    hasListKeywords(query) &&
    query.keywordWatchlistOp === "AND"
  ) {
    return false;
  }
  return true;
}

const uniqueById = <T extends { id?: number }>(items: T[]): T[] => [
  ...new Map(items.map((item) => [item.id, item])).values(),
];

/**
 * The selected combined watchlists as one filter for the search and the live
 * matchers.
 *
 * A list relying on AND is returned as it is: it filters on its own, exactly
 * as before. So is the first of a stale selection holding one, which is what
 * that selection would have got before this change.
 *
 * OR-only lists come back with every operator set to OR, merged when there
 * are several. That changes nothing about a list on its own — an operator
 * that counted as not applying had nothing on one side — but the filter's
 * operators also govern the individual watchlists alongside it
 * (transformSearchParams), where an idle "tags AND tickers" would suddenly
 * bite on their tickers and narrow the result.
 *
 * Keyword expressions are OR-joined. Web-search syntax has no brackets, so
 * each expression is already a set of AND-groups joined by OR (the browser's
 * parseSearchExpression reads it the same way), and joining two keeps every
 * group intact.
 */
export function mergeCombinedFilters(
  queries: CategoryQuery[]
): CategoryQuery | undefined {
  if (queries.length === 0) return undefined;
  if (!queries.every(isOrOnlyCombined)) return queries[0];
  if (queries.length === 1) {
    return {
      ...queries[0],
      filterMode: "OR",
      keywordOp: "OR",
      keywordWatchlistOp: "OR",
    };
  }

  const typed = queries
    .map((query) => query.searchKeyword?.trim())
    .filter((expression): expression is string => !!expression);
  const portfolios = uniqueById(queries.flatMap((q) => q.portfolios ?? []));
  const tagsWatchlists = uniqueById(
    queries.flatMap((q) => q.tagsWatchlists ?? [])
  );

  return {
    id: undefined,
    queryName: queries.map((query) => query.queryName).join(", "),
    categories: [...new Set(queries.flatMap((q) => q.categories ?? []))],
    portfolios,
    portfolioIds: portfolios.map((p) => p.id),
    tagsWatchlists,
    tagsWatchlistIds: tagsWatchlists.map((tw) => tw.id),
    searchKeyword: typed.length > 0 ? typed.join(" OR ") : undefined,
    filterMode: "OR",
    keywordOp: "OR",
    keywordWatchlistOp: "OR",
  };
}
